import { useState } from "react";
import {
  Alert,
  AlertIcon,
  Flex,
  Input,
  Button,
  InputGroup,
  Stack,
  InputLeftElement,
  Image,
  Text,
  Box,
  Link,
  FormControl,
  FormHelperText,
  FormErrorMessage,
  InputRightElement,
} from "@chakra-ui/react";
import { FaUserAlt, FaLock } from "react-icons/fa";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { setToken } from "../Auth.js";
import logo from "../images/logo2.png";
import API_PATH from "../env";
import { clientFieldErrors, readApiError } from "../authHelpers.js";

export default function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError("");
    const errors = clientFieldErrors({ email, password, mode: "login" });
    setFieldErrors(errors);
    if (Object.keys(errors).length) return;

    setBusy(true);
    try {
      const response = await fetch(`${API_PATH}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: email.trim(), password }),
      });
      if (!response.ok) {
        setFormError(await readApiError(response));
        return;
      }
      const body = await response.json();
      setToken(body.token);
      navigate(`/${body.user_id}/todos`);
    } catch {
      setFormError("Network error. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Flex
      flexDirection="column"
      width="100wh"
      minH="100vh"
      backgroundColor="gray.200"
      justifyContent="center"
      alignItems="center"
      px={4}
    >
      <Stack flexDir="column" mb="2" justifyContent="center" alignItems="center" w="100%" maxW="468px">
        <Flex align="center" gap={2} justify="center" mb={2}>
          <Image src={logo} boxSize="60px" alt="" />
          <Text fontSize="3xl" fontWeight="bold">
            WorryLess
          </Text>
        </Flex>
        <Box w="100%" backgroundColor="whiteAlpha.900" boxShadow="md" p={4} borderRadius="md">
          <form onSubmit={handleSubmit}>
            <Stack spacing={4}>
              {formError ? (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  {formError}
                </Alert>
              ) : null}
              <FormControl isInvalid={Boolean(fieldErrors.email)}>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <FaUserAlt color="gray" />
                  </InputLeftElement>
                  <Input
                    type="email"
                    autoComplete="email"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    isDisabled={busy}
                  />
                </InputGroup>
                <FormErrorMessage>{fieldErrors.email}</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={Boolean(fieldErrors.password)}>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <FaLock color="gray" />
                  </InputLeftElement>
                  <Input
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    isDisabled={busy}
                  />
                  <InputRightElement width="4.5rem">
                    <Button h="1.75rem" size="sm" onClick={() => setShowPassword(!showPassword)} type="button">
                      {showPassword ? "Hide" : "Show"}
                    </Button>
                  </InputRightElement>
                </InputGroup>
                <FormHelperText textAlign="right" color="gray.500" fontSize="xs">
                  Forgot your password? Contact the site owner — reset by email is not set up yet.
                </FormHelperText>
                <FormErrorMessage>{fieldErrors.password}</FormErrorMessage>
              </FormControl>
              <Button type="submit" colorScheme="teal" width="full" isLoading={busy} loadingText="Signing in…">
                Sign in
              </Button>
            </Stack>
          </form>
        </Box>
        <Text fontSize="sm">
          New here?{" "}
          <Link as={RouterLink} to="/signup" color="teal.500">
            Create account
          </Link>
          {" · "}
          <Link as={RouterLink} to="/" color="gray.600">
            Home
          </Link>
        </Text>
      </Stack>
    </Flex>
  );
}
